// Mock data for Fortune Auto Design
// Real product catalog loaded from the live catalog store (API-hydrated at
// boot, with data/*.json as the offline fallback — see catalogStore.js).
import { getProducts, getCollections } from './catalogStore';

const collectionsData = getCollections();
const productsData = getProducts();

export const IMAGES = {
  heroCar: 'https://images.pexels.com/photos/20131971/pexels-photo-20131971.jpeg',
  supra: 'https://images.pexels.com/photos/16613452/pexels-photo-16613452.jpeg',
  nsx: 'https://images.pexels.com/photos/30345635/pexels-photo-30345635.jpeg',
  gtr: 'https://images.pexels.com/photos/31043182/pexels-photo-31043182.jpeg',
  carbonTexture: 'https://images.pexels.com/photos/596815/pexels-photo-596815.jpeg',
  carbonHood: 'https://images.pexels.com/photos/17045319/pexels-photo-17045319.jpeg',
  darkGarage: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7',
  workshop: 'https://images.unsplash.com/photo-1551522435-a13afa10f103',
  fabrication: 'https://images.unsplash.com/photo-1615906655593-ad0386982a0f',
  wheelPerf: 'https://images.unsplash.com/photo-1611633235555-45e252fe48c8',
  wheelForged: 'https://images.unsplash.com/photo-1655703870338-7d2584f89729',
  bmwShow: 'https://images.pexels.com/photos/11588669/pexels-photo-11588669.jpeg',
};

export const NAV_LINKS = [
  { label: 'SHOP BY VEHICLE', path: '/shop' },
  { label: 'SHOP BY BRANDS', path: '/body-kits' },
  { label: 'WHEELS', path: '/wheels' },
  { label: 'ABOUT', path: '/about' },
  { label: 'CONTACT', path: '/contact' },
];

export const PARTNER_BRANDS = [
  'DarwinPro', 'Paktechz', 'Sooqoo', 'Karbel', 'Takd Carbon', 'Fortune',
];

// Real collections list (loaded from data/collections.json)
export const COLLECTIONS = collectionsData;

// Group collections by brand
// Brands to exclude from the "Shop by Vehicle" grouping (e.g. wheel brands,
// which are not vehicle makes and have their own dedicated pages).
const NON_VEHICLE_BRANDS = new Set(['Fortune Black Label']);

export const COLLECTIONS_BY_BRAND = COLLECTIONS.reduce((acc, c) => {
  if (NON_VEHICLE_BRANDS.has(c.brand)) return acc;
  acc[c.brand] = acc[c.brand] || [];
  acc[c.brand].push(c);
  return acc;
}, {});

// Featured / popular collections used on home page
export const FEATURED_COLLECTIONS = [
  COLLECTIONS.find((c) => c.id === 'bmw-m3-m4'),
  COLLECTIONS.find((c) => c.id === 'tesla-model-3-highland'),
  COLLECTIONS.find((c) => c.id === 'bmw-m2-g87'),
  COLLECTIONS.find((c) => c.id === 'lamborghini-huracan'),
  COLLECTIONS.find((c) => c.id === 'mercedes-amg-gt'),
].filter(Boolean);

// Real product catalog (loaded from data/products.json) — augmented with default features/specs
const buildFeatures = (p) => {
  if (p.brand === 'Fortune Black Label' && p.category === 'Forged Wheels') {
    return [
      'Monoblock or multi-piece forged construction',
      '6061-T6 forged aluminum',
      'Custom fitment & offset',
      'Multiple finishes available',
      'Load rated for street & track',
    ];
  }
  // Fortune-branded body kits / aero parts are FRP / fiberglass, not carbon.
  if (p.brand === 'Fortune') {
    return [
      'Hand-laid FRP composite construction',
      'Race-tested fitment',
      'Finished ready for paint',
      'Reinforced mounting points',
    ];
  }
  // Everything else (Paktechz, Karbel, Sooqoo, etc.) is dry carbon.
  return [
    'Pre-preg dry carbon fiber',
    'Autoclave cured panels',
    'OEM-grade fitment',
    'UV-resistant clear coat',
  ];
};

const buildSpecs = (p) => {
  const specs = {};
  if (p.brand) specs['Brand'] = p.brand;
  if (p.category) specs['Type'] = p.category;
  if (p.material) {
    // material like "dry-carbon" -> "Dry Carbon"
    const pretty = p.material.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    specs['Material'] = pretty;
  } else if (p.brand === 'Fortune') {
    specs['Material'] = 'FRP';
  } else if (!(p.brand === 'Fortune Black Label' && p.category === 'Forged Wheels')) {
    specs['Material'] = 'Dry Carbon Fiber';
  }
  if (p.vehicle && p.vehicle.modelName) specs['Vehicle'] = p.vehicle.modelName;
  if (p.vehicle && p.vehicle.years) specs['Model Years'] = p.vehicle.years;
  if (p.sku) specs['SKU'] = p.sku;
  if (p.brand === 'Fortune Black Label' && p.category === 'Forged Wheels') {
    specs['Sizes'] = '18" - 22"';
    specs['Finishes'] = 'Matte Black, Gloss Black, Brushed, Custom';
  } else {
    specs['Finish'] = 'Gloss / Matte';
  }
  return specs;
};

export const PRODUCTS = productsData.map((p) => ({
  ...p,
  features: p.features && p.features.length ? p.features : buildFeatures(p),
  specs: p.specs && Object.keys(p.specs).length ? p.specs : buildSpecs(p),
  // Brand-aware default description. Fortune body kits are FRP / fiberglass,
  // Fortune Black Label wheels are forged aluminium, everything else is
  // dry carbon. Avoid auto-tagging Fortune parts with carbon copy.
  description:
    p.description ||
    (p.brand === 'Fortune'
      ? `Premium ${p.category.toLowerCase()} engineered by Fortune. Hand-laid FRP composite, race-tested fitment, finished ready for paint.`
      : p.brand === 'Fortune Black Label'
      ? `Premium ${p.category.toLowerCase()} from Fortune Black Label. Forged 6061-T6 aerospace-grade aluminum, finished to your spec.`
      : `Premium ${p.category.toLowerCase()} engineered by ${p.brand}. Hand-laid dry carbon fiber, autoclave cured, OEM-grade fitment.`),
}));

export const FEATURED_BUILDS = [
  { slug: 'sooqoo-bmw-m3-g8x-v2-rear-diffuser-with-brake-light', title: 'V2 REAR DIFFUSER', subtitle: 'BMW M3 / M4 G8X', price: 1495 },
  { slug: 'paktechz-dry-carbon-hood-bmw-m2-g87-2023-present', title: 'DRY CARBON HOOD', subtitle: 'BMW M2 G87', price: 2495 },
  { slug: 'paktechz-dry-carbon-rear-wing-for-2021-bmw-m3-g80-and-m4-g82-g83', title: 'DRY CARBON REAR WING', subtitle: 'BMW M3 G80 / M4 G82', price: 2195 },
  { slug: 'darwinpro-tesla-model-3-imp-performance-partial-rear-bumper', title: 'IMP REAR BUMPER', subtitle: 'TESLA MODEL 3', price: 2895 },
  { slug: 'paktechz-dry-carbon-rear-bumper-diffuser-porsche-992-turbo', title: 'REAR BUMPER + DIFFUSER', subtitle: 'PORSCHE 992 TURBO', price: 6295 },
  { slug: 'paktechz-dry-carbon-hood-audi-rs6-c8-2020-present', title: 'DRY CARBON HOOD', subtitle: 'AUDI RS6 C8', price: 2495 },
  { slug: 'paktechz-dry-carbon-front-bumper-lamborghini-urus-2023-present', title: 'CARBON FRONT BUMPER', subtitle: 'LAMBORGHINI URUS', price: 6295 },
  { slug: 'paktechz-dry-carbon-rear-diffuser-ferrari-f8-2019-present', title: 'CARBON REAR DIFFUSER', subtitle: 'FERRARI F8', price: 5295 },
];

export const GALLERY_ITEMS = [
  { id: 'g1', title: 'Supra A90 Widebody', category: 'Toyota', image: IMAGES.supra },
  { id: 'g2', title: 'GT-R R35 Carbon Build', category: 'Nissan', image: IMAGES.gtr },
  { id: 'g3', title: 'BMW M4 G82 Full Aero', category: 'BMW', image: IMAGES.bmwShow },
  { id: 'g4', title: 'BMW M2 G87 Carbon', category: 'BMW', image: IMAGES.nsx },
  { id: 'g5', title: 'Tesla Model 3 IMP', category: 'Tesla', image: IMAGES.darkGarage },
  { id: 'g6', title: 'Honda Civic Type R FK8', category: 'Honda', image: IMAGES.nsx },
  { id: 'g7', title: 'Fortune Forged ALPHA', category: 'Wheels', image: IMAGES.wheelForged },
  { id: 'g8', title: 'Custom Workshop Build', category: 'Shop', image: IMAGES.fabrication },
  { id: 'g9', title: 'Carbon Hood Fabrication', category: 'Carbon', image: IMAGES.carbonHood },
];

export const PROCESS_STEPS = [
  { n: '01', title: 'Consultation', desc: 'Reach out with your vision. We discuss goals, timeline, budget and build direction.' },
  { n: '02', title: 'Design', desc: 'Our team drafts renders, spec sheets and part lists tailored to your car.' },
  { n: '03', title: 'Fabrication', desc: 'Precision fabrication in-house \u2014 carbon layup, autoclave curing, CNC machining.' },
  { n: '04', title: 'Fitment', desc: 'Every panel is test-fit to ensure OEM-grade alignment before finishing.' },
  { n: '05', title: 'Finishing', desc: 'Finish coat, clear coat, or custom paint. Hardware and install guides included.' },
  { n: '06', title: 'Delivery', desc: 'White-glove crate packing and worldwide shipping. Install support included.' },
];

export const SERVICES = [
  { id: 'carbon', title: 'Carbon Fiber Parts', desc: 'Pre-preg dry carbon fiber components \u2014 hoods, lips, diffusers, splitters \u2014 engineered for perfect fitment and track-grade strength.', icon: 'Layers' },
  { id: 'fab', title: 'Custom Fabrication', desc: 'Bespoke metal & composite fabrication \u2014 roll cages, exhausts, one-off panels, prototype aero. If you can dream it, we can build it.', icon: 'Wrench' },
  { id: 'aero', title: 'Aero & Styling', desc: 'Functional aero packages that balance downforce, drag and style. Designed in CFD, fabricated to OEM tolerances.', icon: 'Car' },
  { id: 'perf', title: 'Performance Focused', desc: 'Every build is a performance build. We dial in suspension, wheels, brakes and power to match your aero and chassis.', icon: 'Gauge' },
];

export const WHY_US = [
  { title: 'PREMIUM QUALITY', desc: 'Top-tier materials and uncompromising craftsmanship.', icon: 'ShieldCheck' },
  { title: 'PROVEN RESULTS', desc: 'Trusted by enthusiasts and professionals worldwide.', icon: 'Trophy' },
  { title: 'CUSTOM SOLUTIONS', desc: 'Every project is custom designed to your goals.', icon: 'Settings' },
  { title: 'DEDICATED SUPPORT', desc: 'We\u2019re here to help from start to finish and beyond.', icon: 'Headphones' },
];

export const COMPANY_INFO = {
  name: 'Fortune Auto Design',
  shortName: 'Fortune',
  tagline: 'Precision. Performance. Style.',
  email: 'info@fortuneautodesign.com',
  // Inquiries are sent to all addresses in `inquiryRecipients`
  inquiryRecipients: ['info@fortuneautodesign.com'],
  address: 'Toronto, Ontario, Canada',
  hours: 'Mon\u2013Fri 9:00am \u2013 6:00pm EST',
  socials: {
    instagram: 'https://www.instagram.com/fortuneautodesign',
    facebook: 'https://www.facebook.com/FortuneAutoDesign/',
    tiktok: 'https://tiktok.com/@fortuneautodesign',
    youtube: 'https://youtube.com',
  },
};
